import styled from "@emotion/styled";
import { useClerk } from "@clerk/clerk-react";
import { COLOR_APP_TEXT } from "../theme.ts";
import Signout from "../icons/Signout.tsx";
import { useState } from "react";
import { ParagraphHeaderSmall } from "../typography/Paragraph.tsx";
import { SecondaryButtonSmall } from "../buttons/SecondaryButton.tsx";
import Divider from "../Divider.tsx";
import HelloprenupLogoWithBlueBackground from "../icons/HelloprenupLogoWithBlueBackground.tsx";
import HelloprenupLogoWithText from "../icons/HelloprenupLogoWithText.tsx";
import Footer from "./Footer.tsx";
import { useNavItems, useBottomNavItems } from "./utils/nav.tsx";
import {
  StyledCircleIconButton,
  SignoutAndFooter,
  NavLogo,
  IconContainer,
  HelloPrenupLogoWithTextContainer,
  FooterContainer,
  Link,
} from "./utils/desktop/components.tsx";
import WarningPopup from "../popups/Warning.tsx";
import SectionList from "./SectionList.tsx";
import { useDispatch } from "react-redux";
import NavBar from "./utils/desktop/NavBar.tsx";
import NavItemsContainer from "./utils/desktop/NavItemsContainer.tsx";
import NavItem from "./utils/desktop/NavItem.tsx";
import { useLocation } from "react-router";
import NavItemNumber from "./utils/desktop/NavItemNumber.tsx";

const NavItemGroup = styled.div<{ isDisabled?: boolean | undefined }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: ${(props) => (props.isDisabled ? "none" : "auto")};
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  cursor: ${(props) => (props.isDisabled ? "default" : "pointer")};
`;

const Nav = () => {
  const { signOut } = useClerk();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const navItems = useNavItems();
  const bottomNavItems = useBottomNavItems();
  const dispatch = useDispatch();
  const location = useLocation();

  const handleLogout = () => {
    setIsExpanded(false);
    setShowLogoutPopup(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutPopup(false);
    dispatch({ type: "USER_LOGOUT" });
    await signOut({ redirectUrl: "/login" });
  };

  const handleCancelLogout = () => {
    setShowLogoutPopup(false);
  };

  return (
    <>
      {showLogoutPopup && (
        <WarningPopup
          title="Are you sure you want to log out?"
          description="Remember, you must complete your prenup before your marriage date!"
          buttons={{
            confirmText: "Log out",
            cancelText: "Cancel",
            onConfirm: handleConfirmLogout,
            onCancel: handleCancelLogout,
          }}
        />
      )}
      <NavBar
        role="navigation"
        isExpanded={isExpanded}
        onMouseOver={() => {
          if (!isExpanded) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
      >
        <NavLogo isExpanded={isExpanded}>
          {isExpanded ? (
            <HelloPrenupLogoWithTextContainer>
              <HelloprenupLogoWithText />
            </HelloPrenupLogoWithTextContainer>
          ) : (
            <HelloprenupLogoWithBlueBackground />
          )}
        </NavLogo>
        <NavItemsContainer>
          {navItems.map((item) => (
            <NavItemGroup key={item.label} isDisabled={item.isDisabled}>
              <Link
                to={item.sections?.[0] ? item.sections[0].path : item.path}
                key={item.label}
              >
                <NavItem
                  key={item.label}
                  isActive={
                    !!(
                      location.pathname === item.path ||
                      item.sections?.some(
                        (section) => location.pathname === section.path,
                      )
                    )
                  }
                  isExpanded={isExpanded}
                >
                  <IconContainer>
                    {isExpanded && item.order ? (
                      <NavItemNumber
                        isActive={
                          !!(
                            location.pathname === item.path ||
                            item.sections?.some(
                              (section) => location.pathname === section.path,
                            )
                          )
                        }
                      >
                        {item.order}
                      </NavItemNumber>
                    ) : (
                      item.icon
                    )}
                  </IconContainer>
                  {isExpanded && (
                    <ParagraphHeaderSmall color={COLOR_APP_TEXT}>
                      {item.label}
                    </ParagraphHeaderSmall>
                  )}
                </NavItem>
              </Link>

              {/* Render sections as separate items underneath */}
              {isExpanded &&
                item.sections &&
                item.sections.length > 0 &&
                location.pathname.includes(item.path) && (
                  <SectionList
                    sections={item.sections}
                    error={item.sectionsError ?? ""}
                    loading={item.sectionsLoading}
                    setIsExpanded={setIsExpanded}
                  />
                )}
            </NavItemGroup>
          ))}
        </NavItemsContainer>
        <SignoutAndFooter>
          <Divider />
          {bottomNavItems.map((item) => (
            <NavItemGroup key={item.label} isDisabled={item.isDisabled}>
              <Link to={item.path} key={item.label}>
                <NavItem
                  key={item.label}
                  isActive={location.pathname === item.path}
                  isExpanded={isExpanded}
                >
                  <IconContainer>{item.icon}</IconContainer>
                  {isExpanded && (
                    <ParagraphHeaderSmall color={COLOR_APP_TEXT}>
                      {item.label}
                    </ParagraphHeaderSmall>
                  )}
                </NavItem>
              </Link>
            </NavItemGroup>
          ))}
          {isExpanded ? (
            <SecondaryButtonSmall onClick={handleLogout}>
              Sign&nbsp;out
            </SecondaryButtonSmall>
          ) : (
            <StyledCircleIconButton>
              <Signout />
            </StyledCircleIconButton>
          )}
          {isExpanded && (
            <FooterContainer>
              <Footer />
            </FooterContainer>
          )}
        </SignoutAndFooter>
      </NavBar>
    </>
  );
};

export default Nav;
